import React, { useState } from "react";
import { User, Gift, Clock, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

/**
 * GuestPortalExtras - The three extended guest-portal tabs:
 *  - "profile": read-only My Data view (GDPR Art. 15) + staff-mediated
 *    data-change request form (Art. 16 — guests CANNOT self-edit).
 *  - "requests": early check-in / late check-out request — explicitly
 *    availability-dependent, decided by reception.
 *  - "rewards": placeholder reward catalog with point redemption
 *    (server-side deduction + trace for staff handout).
 *
 * All POSTs go through /api/guest?action=... with the Supabase session
 * token; the backend creates Rezeption traces (same pattern as the
 * invoice-correction flow).
 */

// Placeholder reward catalog — labels/costs mirrored server-side in
// api/guest.js REWARDS (server values are authoritative for deduction).
// Not exported: react-refresh requires component files to only export
// components; the catalog is render-local.
const REWARD_CATALOG = [
  { id: "welcome-drink", cost: 50, labelDe: "Begrüßungsgetränk an der Bar", labelEn: "Welcome drink at the bar", icon: "🥂" },
  { id: "late-checkout-2h", cost: 150, labelDe: "Late Check-Out bis 14:00 (nach Verfügbarkeit)", labelEn: "Late check-out until 14:00 (subject to availability)", icon: "🕐" },
  { id: "breakfast-voucher", cost: 250, labelDe: "Frühstücksgutschein für 2 Personen", labelEn: "Breakfast voucher for 2 persons", icon: "🥐" },
  { id: "upgrade", cost: 600, labelDe: "Zimmer-Upgrade (nach Verfügbarkeit)", labelEn: "Room upgrade (subject to availability)", icon: "⭐" },
  { id: "free-night", cost: 2000, labelDe: "1 kostenlose Übernachtung", labelEn: "1 free night", icon: "🛏️" },
];

interface GuestPortalExtrasProps {
  activeTab: "profile" | "requests" | "rewards";
  profile: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    street?: string;
    zipCode?: string;
    city?: string;
    country?: string;
  } | null;
  loyaltyPoints: number;
  reservations: any[];
  onPointsChange: (newPoints: number) => void;
  getToken: () => Promise<string | null>;
  t: (key: string, opts?: Record<string, unknown>) => string;
  language: string;
}

type SubmitState = "idle" | "submitting" | "success" | "error";

export default function GuestPortalExtras({
  activeTab, profile, loyaltyPoints, reservations, onPointsChange, getToken, t, language,
}: GuestPortalExtrasProps) {
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [message, setMessage] = useState("");
  const [busyRewardId, setBusyRewardId] = useState<string | null>(null);

  // --- Data-change form state (only fields the guest may request) ---
  const [changeForm, setChangeForm] = useState({
    firstName: "", lastName: "", phone: "", street: "", zipCode: "", city: "", country: "", notes: "",
  });

  // --- Stay request form state ---
  // Owner decision (2026-10-16): only Confirmed reservations are requestable.
  const upcoming = reservations.filter((r: { status?: string }) => r.status === "Confirmed");
  const [stayForm, setStayForm] = useState({
    reservationId: "",
    requestType: "early_checkin",
    preferredTime: "",
    notes: "",
  });

  // Keep the select in sync when the list loads/changes after mount.
  const effectiveReservationId = stayForm.reservationId || upcoming[0]?.id || "";

  const postAction = async (action: string, body: Record<string, unknown>) => {
    const token = await getToken();
    const res = await fetch(`/api/guest?action=${action}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Request failed");
    return data;
  };

  const handleDataChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasAny = Object.values(changeForm).some((v) => v.trim() !== "");
    if (!hasAny) {
      setSubmitState("error");
      setMessage(t("guestPortal.dataChangeEmpty"));
      return;
    }
    setSubmitState("submitting");
    setMessage("");
    try {
      // Only send non-empty fields (strict schema rejects empty strings
      // only implicitly — filtering keeps the trace description clean).
      const payload = Object.fromEntries(
        Object.entries(changeForm).filter(([, v]) => v.trim() !== "")
      );
      await postAction("data-change", payload);
      setSubmitState("success");
      setMessage(t("guestPortal.dataChangeSuccess"));
      setChangeForm({ firstName: "", lastName: "", phone: "", street: "", zipCode: "", city: "", country: "", notes: "" });
    } catch (err) {
      setSubmitState("error");
      setMessage(err instanceof Error ? err.message : String(err));
    }
  };

  const handleStayRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const reservationId = stayForm.reservationId || upcoming[0]?.id || "";
    if (!reservationId) return;
    setSubmitState("submitting");
    setMessage("");
    try {
      const payload: Record<string, unknown> = {
        reservationId,
        requestType: stayForm.requestType,
      };
      if (stayForm.preferredTime.trim()) payload.preferredTime = stayForm.preferredTime.trim();
      if (stayForm.notes.trim()) payload.notes = stayForm.notes.trim();
      await postAction("stay-request", payload);
      setSubmitState("success");
      setMessage(t("guestPortal.stayRequestSuccess"));
      setStayForm((prev) => ({ ...prev, preferredTime: "", notes: "" }));
    } catch (err) {
      setSubmitState("error");
      setMessage(err instanceof Error ? err.message : String(err));
    }
  };

  const handleRedeem = async (rewardId: string) => {
    setBusyRewardId(rewardId);
    setMessage("");
    try {
      const data = await postAction("redeem", { rewardId });
      if (!data.success) {
        if (data.reason === "insufficient_points") {
          setSubmitState("error");
          setMessage(t("guestPortal.rewardsNotEnough", { missing: Math.ceil(data.required - data.currentPoints) }));
        } else if (data.reason === "balance_changed") {
          setSubmitState("error");
          setMessage(t("guestPortal.rewardsBalanceChanged"));
        }
        return;
      }
      setSubmitState("success");
      setMessage(
        `${t("guestPortal.rewardsSuccess")} ${t("guestPortal.rewardsNewBalance", { points: data.newBalance })}`
      );
      onPointsChange(data.newBalance);
    } catch (err) {
      setSubmitState("error");
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyRewardId(null);
    }
  };

  const ProfileRow = ({ label, value }: { label: string; value?: string }) => (
    <div className="flex justify-between items-start gap-4 py-2.5 border-b border-border last:border-0">
      <span className="text-[10px] font-black uppercase tracking-widest text-muted shrink-0">{label}</span>
      <span className="text-xs font-bold text-fg text-right break-words">{value || t("guestPortal.profileNotProvided")}</span>
    </div>
  );

  const MessageBanner = submitState !== "idle" && message && (
    <div
      className={`flex items-center gap-2 p-3 rounded-xl text-xs font-bold border ${
        submitState === "success"
          ? "bg-succ-muted text-succ border-succ/30"
          : submitState === "error"
            ? "bg-err-muted text-err border-err/30"
            : "bg-accent-muted text-accent border-accent/30"
      }`}
    >
      {submitState === "success" ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
      {message}
    </div>
  );

  // --- TAB: PROFILE (My Data) ---
  if (activeTab === "profile") {
    return (
      <div className="space-y-6">
        <div className="glass-card p-6 md:p-8">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-xl bg-accent-muted text-accent flex items-center justify-center">
              <User size={18} />
            </span>
            <h2 className="text-xl md:text-2xl font-black display-font uppercase tracking-tight text-fg">
              {t("guestPortal.profileTitle")}
            </h2>
          </div>
          <p className="text-xs text-muted mt-2 mb-6">{t("guestPortal.profileDescription")}</p>

          <div className="max-w-xl">
            <ProfileRow label={t("guestPortal.profileName")} value={[profile?.firstName, profile?.lastName].filter(Boolean).join(" ")} />
            <ProfileRow label={t("guestPortal.profileEmail")} value={profile?.email} />
            <ProfileRow label={t("guestPortal.profilePhone")} value={profile?.phone} />
            <ProfileRow
              label={t("guestPortal.profileAddress")}
              value={[profile?.street, [profile?.zipCode, profile?.city].filter(Boolean).join(" "), profile?.country].filter(Boolean).join(", ")}
            />
          </div>
        </div>

        <div className="glass-card p-6 md:p-8">
          <h3 className="text-sm font-black uppercase tracking-wider text-fg mb-1">{t("guestPortal.dataChangeTitle")}</h3>
          <p className="text-xs text-muted mb-4">{t("guestPortal.dataChangeDescription")}</p>
          {MessageBanner}
          <form onSubmit={handleDataChangeSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {([
              ["firstName", t("guestPortal.profileName")],
              ["phone", t("guestPortal.profilePhone")],
              ["street", t("guestPortal.profileAddress")],
              ["zipCode", "PLZ / ZIP"],
              ["city", "Stadt / City"],
              ["country", "Land / Country"],
            ] as const).map(([field, label]) => (
              <label key={field} className="flex flex-col gap-1">
                <span className="text-[9px] font-black uppercase tracking-widest text-muted">{label}</span>
                <input
                  type="text"
                  value={changeForm[field]}
                  onChange={(e) => setChangeForm((prev) => ({ ...prev, [field]: e.target.value }))}
                  className="bg-bg border border-border rounded-xl px-3 py-2 text-xs font-bold text-fg outline-hidden focus:border-accent focus:ring-2 focus:ring-accent-muted"
                />
              </label>
            ))}
            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-[9px] font-black uppercase tracking-widest text-muted">Notes / Anmerkungen</span>
              <textarea
                rows={2}
                value={changeForm.notes}
                onChange={(e) => setChangeForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="bg-bg border border-border rounded-xl px-3 py-2 text-xs font-bold text-fg outline-hidden focus:border-accent focus:ring-2 focus:ring-accent-muted resize-none"
              />
            </label>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={submitState === "submitting"}
                className="hub-btn hub-btn-primary inline-flex items-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {submitState === "submitting" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {t("guestPortal.dataChangeSubmit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // --- TAB: REQUESTS (early check-in / late check-out) ---
  if (activeTab === "requests") {
    return (
      <div className="space-y-6">
        <div className="glass-card p-6 md:p-8">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-xl bg-accent-muted text-accent flex items-center justify-center">
              <Clock size={18} />
            </span>
            <h2 className="text-xl md:text-2xl font-black display-font uppercase tracking-tight text-fg">
              {t("guestPortal.stayRequestTitle")}
            </h2>
          </div>

          {/* Availability notice — emphasized per owner requirement */}
          <div className="mt-4 flex items-start gap-2 p-4 rounded-xl bg-warn-muted border border-warn/30 text-xs font-bold text-warn">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            {t("guestPortal.stayRequestNotice")}
          </div>

          <p className="text-xs text-muted mt-4 mb-5">{t("guestPortal.requestsHistoryNote")}</p>
          {MessageBanner}

          {upcoming.length === 0 ? (
            <div className="text-center py-8 text-muted text-xs mt-4">{t("guestPortal.stayRequestNoReservations")}</div>
          ) : (
            <form onSubmit={handleStayRequestSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <label className="flex flex-col gap-1">
                <span className="text-[9px] font-black uppercase tracking-widest text-muted">{t("guestPortal.stayRequestType")}</span>
                <select
                  value={stayForm.requestType}
                  onChange={(e) => setStayForm((prev) => ({ ...prev, requestType: e.target.value }))}
                  className="bg-bg border border-border rounded-xl px-3 py-2 text-xs font-bold text-fg outline-hidden focus:border-accent cursor-pointer"
                >
                  <option value="early_checkin">{t("guestPortal.earlyCheckin")}</option>
                  <option value="late_checkout">{t("guestPortal.lateCheckout")}</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[9px] font-black uppercase tracking-widest text-muted">{t("guestPortal.stayRequestReservation")}</span>
                <select
                  value={effectiveReservationId}
                  onChange={(e) => setStayForm((prev) => ({ ...prev, reservationId: e.target.value }))}
                  className="bg-bg border border-border rounded-xl px-3 py-2 text-xs font-bold text-fg outline-hidden focus:border-accent cursor-pointer"
                >
                  {upcoming.map((r: any) => (
                    <option key={r.id} value={r.id}>
                      {r.propertyName || t("guestPortal.fallbackHotelName")} — {new Date(r.arrival).toLocaleDateString()} ({r.id})
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[9px] font-black uppercase tracking-widest text-muted">{t("guestPortal.stayRequestTime")}</span>
                <input
                  type="text"
                  value={stayForm.preferredTime}
                  onChange={(e) => setStayForm((prev) => ({ ...prev, preferredTime: e.target.value }))}
                  placeholder={stayForm.requestType === "early_checkin" ? "z.B. 12:00 / e.g. 12:00" : "z.B. 15:00 / e.g. 15:00"}
                  className="bg-bg border border-border rounded-xl px-3 py-2 text-xs font-bold text-fg outline-hidden focus:border-accent"
                />
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-[9px] font-black uppercase tracking-widest text-muted">{t("guestPortal.stayRequestNotes")}</span>
                <textarea
                  rows={2}
                  value={stayForm.notes}
                  onChange={(e) => setStayForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="bg-bg border border-border rounded-xl px-3 py-2 text-xs font-bold text-fg outline-hidden focus:border-accent resize-none"
                />
              </label>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={submitState === "submitting"}
                  className="hub-btn hub-btn-primary inline-flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                >
                  {submitState === "submitting" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {t("guestPortal.stayRequestSubmit")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // --- TAB: REWARDS ---
  return (
    <div className="space-y-6">
      <div className="glass-card p-6 md:p-8">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-10 h-10 rounded-xl bg-accent-muted text-accent flex items-center justify-center">
            <Gift size={18} />
          </span>
          <h2 className="text-xl md:text-2xl font-black display-font uppercase tracking-tight text-fg">
            {t("guestPortal.rewardsTitle")}
          </h2>
        </div>
        <p className="text-xs text-muted mt-2">{t("guestPortal.rewardsDescription")}</p>
        <p className="mt-2 text-sm font-black text-accent">
          {t("guestPortal.pointsDisplay", { points: loyaltyPoints })}
        </p>

        <div className="mt-4">{MessageBanner}</div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
          {REWARD_CATALOG.map((reward) => {
            const affordable = loyaltyPoints >= reward.cost;
            const busy = busyRewardId === reward.id;
            return (
              <div
                key={reward.id}
                className={`flex items-center justify-between gap-4 p-4 rounded-2xl border transition-all ${
                  affordable
                    ? "bg-surface border-border hover:border-accent"
                    : "bg-bg border-border opacity-70"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl shrink-0">{reward.icon}</span>
                  <div className="min-w-0">
                    <p className="text-xs font-black text-fg leading-snug">
                      {language === "en" ? reward.labelEn : reward.labelDe}
                    </p>
                    <p className="text-[10px] font-black text-accent mt-0.5">
                      {reward.cost} {t("guestPortal.rewardsCost")}
                    </p>
                    {!affordable && (
                      <p className="text-[9px] font-bold text-err mt-0.5">
                        {t("guestPortal.rewardsNotEnough", { missing: reward.cost - Math.floor(loyaltyPoints) })}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRedeem(reward.id)}
                  disabled={!affordable || busy}
                  className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider whitespace-nowrap transition-all cursor-pointer ${
                    affordable
                      ? "hub-btn hub-btn-primary"
                      : "bg-surface-subtle text-muted border border-border cursor-not-allowed"
                  } ${busy ? "opacity-50" : ""}`}
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : t("guestPortal.rewardsRedeem")}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

